import {
  BufferAttribute,
  Color,
  Group,
  Matrix4,
  Points,
  Ray,
  Raycaster,
  Vector3,
  Vector4,
} from "three";
import { IPointcloudSectionSelection } from "./PointCloud";
import { IPointCloudTile } from "./PointCloudTile";
import { PointCloudMaterial } from "../utils/PointCloudMaterial";
import { Viewer } from "../viewer";
import { ORIGINAL } from "../config/ViewerConfig";

export class PointCloudBVH {
  private _viewer: Viewer;
  private _rootTile: IPointCloudTile;
  private _flatMapUrlToTile: Map<string, IPointCloudTile>; // tile url to all available tileMap
  // private _tileMapPickIndexToUrl: Map<number, string>; // tile url to all available tileMap
  private _offset: Vector3;

  get offset(): Vector3 {
    return this._offset;
  }

  set offset(o: Vector3) {
    this._offset.copy(o);
  }

  get rootTile(): IPointCloudTile {
    return this._rootTile;
  }

  getFlatMapUrlToTile() {
    return this._flatMapUrlToTile;
  }

  setRootTile(tile: IPointCloudTile) {
    this._rootTile = tile;
    this._flatMapUrlToTile.clear();

    // fillup flatmap
    const fillFlatMap = (_t: IPointCloudTile) => {
      for (const c of _t.children) {
        fillFlatMap(c);
      }
      this._flatMapUrlToTile.set(_t.url, _t);
    };
    fillFlatMap(this._rootTile);
  }

  setRootTileForOriginal(tile: IPointCloudTile) {
    this._rootTile = tile;
    this._flatMapUrlToTile.clear();

    // fillup flatmap
    this._flatMapUrlToTile.set(ORIGINAL, tile);
  }

  constructor(viewer: Viewer) {
    this._viewer = viewer;
    this._flatMapUrlToTile = new Map();
    this._offset = new Vector3();
  }

  public clear() {
    this.disposeHierchy(this._rootTile);
    this._rootTile = undefined;
    this._flatMapUrlToTile.clear();
    this._offset.set(0, 0, 0);
  }

  public disposeHierchy(node: IPointCloudTile) {
    if (!node) return;
    node.pointcloud?.dispose();
    for (const child of node.children) {
      this.disposeHierchy(child);
    }
  }

  public intersectPointCloud(
    // singleRender: (pickFlag: boolean) => void,
    tileMap: Map<number, string>,
    gl: WebGLRenderingContext,
    pickCoord: { x: number; y: number }
    // type: string | undefined
  ): Vector3 | undefined {
    for (const url of tileMap.values()) {
      this._flatMapUrlToTile.get(url).pointcloud.setPickMaterial(true);
    }

    const pickWindowSize = 1;

    const pixels = new Uint8Array(pickWindowSize * pickWindowSize * 4);

    this._viewer.singleRender();

    gl.readPixels(
      pickCoord.x,
      pickCoord.y,
      pickWindowSize,
      pickWindowSize,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixels
    );

    // console.log("---");
    // console.log("pixels: ", pixels);
    // console.log(tileMap);
    // console.log(this._flatMapUrlToTile);

    for (const url of tileMap.values()) {
      this._flatMapUrlToTile.get(url).pointcloud.setPickMaterial(false);
    }

    this._viewer.singleRender();

    try {
      const alpha = pixels[3];

      // 背景をはじく
      // if ((pixels[0] === 0 && pixels[1] === 0 && pixels[2] === 0 && pixels[3] === 0)) {
      //   throw new Error();
      // }

      if (255 < alpha) {
        console.error("Too many tiles.");
        throw new Error();
      }

      pixels[3] = 0;

      let ibuffer = new Uint32Array(pixels.buffer);

      const position = this._flatMapUrlToTile
        .get(tileMap.get(alpha))
        .pointcloud.getMesh()
        .geometry.getAttribute("position").array;

      const x = position[3 * ibuffer[0] + 0];
      const y = position[3 * ibuffer[0] + 1];
      const z = position[3 * ibuffer[0] + 2];

      const xyz = new Vector3(x, y, z);

      // console.log("tileMap: ", tileMap);
      // console.log("flatMap", this._flatMapUrlToTile);
      // console.log("alpha: ", alpha);
      // console.log("tileMap.get(alpha): ", tileMap.get(alpha));
      // console.log("ibuffer[0]: ", ibuffer[0]);
      // console.log("position: ", position);
      // console.log("xyz: ", xyz);
      return xyz;
    } catch (err) {
      // console.log("err: ", { err });
      return undefined;
    }
  }

  public intersectPointCloudFromRay(raycaster: Raycaster): Vector3 | undefined {
    //Todo need to get the nearest point
    //sort by distance
    if (!this._rootTile) {
      console.warn("Pointcloud not found");
      return;
    }

    const possiblePointClouds: Points[] = [];

    const _intersectsPointCloud = (_tile: IPointCloudTile, ray: Ray): void => {
      if (_tile.intersects(ray)) {
        if (_tile.pointcloud) {
          possiblePointClouds.push(_tile.pointcloud.getMesh());
        }
        for (const child of _tile.children) {
          _intersectsPointCloud(child, ray);
        }
      }
    };

    //collect the possible pointclouds
    for (const c of this._rootTile.children) {
      _intersectsPointCloud(c, raycaster.ray);
    }

    if (this._rootTile.pointcloud) {
      possiblePointClouds.push(this._rootTile.pointcloud.getMesh());
    }

    const hits = raycaster.intersectObjects(possiblePointClouds);

    if (hits.length > 0) {
      return hits[0].point;
    }

    return;
  }

  setVisibility(visibility: boolean) {
    this._rootTile.setVisibility(visibility);
  }

  public setPointSize(size: number) {
    this._rootTile.setPointSize(size);
  }

  public removeSelectedPoints(selectionColor: Color) {
    this._rootTile.removeSelectedPoints(selectionColor);
  }

  public resetPointCloudColor() {
    this._rootTile?.resetColor();
  }
}
